import { SiteSelect } from "components/form/SiteSelect";
import PageLoader from "components/general/PageLoader";
import PopupModal from "components/general/PopupModal";
import ItemListItem from "components/item/ItemListItem";
import Pagination from "components/navigation/Pagination";
import { Box, Button, Input, InputField, InputSlot, Text, VStack } from "components/ui";
import { H3 } from "components/ui/Typography";
import { Plus, Check } from "lucide-react-native";
import { ActivityIndicator } from "react-native";
import React from "react";
import { useGetItemQuery, usePutCatalogueByIdMutation, usePutTemplateByIdMutation } from "store/abcoApi";
import { debounce } from "utils/globalFunctions";
import Toast from "react-native-toast-notifications";

type Props = {
	catalogue: any;
	removeStockcode: (stockcode: { stockcode?: string; id: number }) => void;
	addStockcode: (stockcode: { stockcode?: string; id: number }) => void;
	updateStockcode?: (stockcode: { stockcode?: string; id: number; isRechargeable: boolean }) => void;
	setPageNumber?: any;
	setSearchFilterTerm: any;
	searchFilterTerm: any;
	type: "catalogues" | "stocklists";
};

export default function StockCollectionDetailScreen({ catalogue, removeStockcode, addStockcode, setPageNumber, updateStockcode, setSearchFilterTerm, searchFilterTerm, type }: Props) {
	const [searchTerm, setSearchTerm] = React.useState("");
	const [addNew, setAddNew] = React.useState(false);
	const [itemSearchTerm, setItemSearchTerm] = React.useState("");
	const [localSearchTerm, setLocalSearchTerm] = React.useState(searchFilterTerm);
	const [postCatalogue, { isLoading: isMutating }] = usePutCatalogueByIdMutation();
	const [postTemplate, { isLoading: isTemplateMutating }] = usePutTemplateByIdMutation();

	const [loadingItems, setLoadingItems] = React.useState<{ [key: string]: boolean }>({});
	const toastRef = React.useRef<any>(null);

	const debouncedSetSearchFilterTerm = React.useCallback(
		debounce((text: string) => {
			setSearchFilterTerm(text);
		}, 300),
		[setSearchFilterTerm],
	);

	React.useEffect(() => {
		return () => {
			debouncedSetSearchFilterTerm.cancel();
		};
	}, [debouncedSetSearchFilterTerm]);

	React.useEffect(() => {
		setLocalSearchTerm(searchFilterTerm);
	}, [searchFilterTerm]);

	// Filter items based on search term
	const filteredItems = React.useMemo(() => {
		if (!searchFilterTerm) return catalogue?.lineCollection?.list || [];

		return (catalogue?.lineCollection?.list || []).filter((line: any) => line.name?.toLowerCase().includes(searchFilterTerm.toLowerCase()) || line.stockcode?.toLowerCase().includes(searchFilterTerm.toLowerCase()));
	}, [catalogue?.lineCollection?.list, searchFilterTerm]);

	const totalPages = catalogue?.lineCollection?.metaData?.pageCount || 1;

	const setSite = (value: any) => {
		var catalogueToUpdate = { ...catalogue };
		catalogueToUpdate.groupCollection = value.map((item: any) => {
			return { id: item.id, name: "", userGroup: [], catalogueCollection: [], approvedStocklistCollection: [], approverCollection: [], purchaseOrderRegex: "" };
		});

		var postData = {
			id: Number(catalogueToUpdate.id),
			stockCollection: catalogueToUpdate,
		};

		switch (type) {
			case "catalogues":	
				postCatalogue(postData);
				break;

			case "stocklists":
				postTemplate(postData);
				break;
		};
	}

	const {
		data: searchItemCollection,
		isLoading,
		isFetching,
		isError,
	} = useGetItemQuery(
		{
			searchTerm: searchTerm,
			pageNumber: 1,
			pageSize: 5,
		},
		{
			skip: searchTerm.length === 0,
		},
	);

	const handleAddStockcode = async (stockcode: { stockcode?: string; id: number }) => {
		if (!stockcode.stockcode) return;

		try {
			setLoadingItems((prev) => ({ ...prev, [stockcode.stockcode!]: true }));
			let res: any = await addStockcode(stockcode);
			console.log("res", res.error.status);
			if (res.error.status === 400) {
				toastRef.current?.show("This item is already in the list", {
					type: "danger",
					textStyle: { fontSize: 20 },
					placement: "bottom",
				});
			}
			// Wait for the item to be in the list before clearing loading state
			const checkItemAdded = () => {
				return catalogue?.lineCollection?.list?.some((item: any) => item.stockcode === stockcode.stockcode);
			};

			// Poll for the item to be added
			let attempts = 0;
			while (!checkItemAdded() && attempts < 10) {
				await new Promise((resolve) => setTimeout(resolve, 100));
				attempts++;
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingItems((prev) => ({ ...prev, [stockcode.stockcode!]: false }));
		}
	};

	const handleRechargeableChange = async (stockcode: string, newValue: boolean) => {
		if (!updateStockcode) {
			return;
		}

		try {
			await updateStockcode({
				stockcode: stockcode,
				id: Number(catalogue.id),
				isRechargeable: newValue,
			});
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<Box gap={20}>
			<Box flexDirection="row" alignItems="center" justifyContent="space-between">
				<H3>{catalogue.name}</H3>
				<Box flexDirection="row" gap={12}>
					<SiteSelect minWidth={250} onSelect={setSite} value={catalogue.groupCollection} isMulti={true} allowedTypes="Both" matchMode="Group" title="Limit to site/group" />
					<Button borderRadius={12} onPress={() => setAddNew(!addNew)} mt={0} variant="primary">
						<Text color="$basicwhite">Add item</Text>
					</Button>
				</Box>
			</Box>
			<Box>
				<Input px="$0" borderWidth={1} borderRadius={12} py="$3">
					<InputField
						borderWidth={0}
						fontSize={16}
						borderRadius="$lg"
						placeholder="Search items..."
						value={localSearchTerm}
						onChangeText={(val) => {
							setLocalSearchTerm(val);
							debouncedSetSearchFilterTerm(val);
						}}
						color="$basicblack"
						placeholderTextColor="#cccccc"
					/>
				</Input>
			</Box>
			<PopupModal viewHeight={"auto"} animationIn={"fadeIn"} animationOut={"fadeOut"} scrollOffset={1} onBackdropPress={() => setAddNew(false)} isVisible={addNew} title={`Add item to ${catalogue.name}`}>
				<Toast ref={toastRef} />
				<Text mb={12} sx={{ fontFamily: "$robotoBold" }}>
					Search for items:
				</Text>
				<Box flexDirection="row" alignItems="center">
					<Input flex={1} px="$0" borderWidth={1} borderRadius={12} py="$3" isDisabled={false} isInvalid={false} isReadOnly={false} isFocused={true} isHovered={true}>
						<InputField borderWidth={0} fontSize={16} borderRadius={"$lg"} placeholder={"Type here..."} value={searchTerm} onChangeText={setSearchTerm} color="$basicblack" placeholderTextColor={"#cccccc"} />
						<InputSlot></InputSlot>
					</Input>
				</Box>
				<VStack gap={24} paddingVertical={24}>
					{isLoading || isFetching ? (
						<Box minHeight={150}>
							<PageLoader />
						</Box>
					) : isError ? (
						<Text color="$error">Error loading search results</Text>
					) : (
						searchTerm.length > 0 &&
						(searchItemCollection?.list?.length ? (
							searchItemCollection.list.map((item, index) => (
								<Box key={index} flexDirection="row" justifyContent="space-between" alignItems="center">
									<Text sx={{ fontFamily: "$robotoBold" }}>
										{item.name} - {item.stockcode}
									</Text>
									<Button
										variant="secondary"
										gap={6}
										onPress={() =>
											handleAddStockcode({
												stockcode: item.stockcode || undefined,
												id: Number(catalogue.id),
											})
										}
										disabled={loadingItems[item.stockcode || ""]}
									>
										{loadingItems[item.stockcode || ""] ? <ActivityIndicator size="small" color="black" /> : <Plus color={"black"} />}
										<Text>{loadingItems[item.stockcode || ""] ? "Adding..." : "Add"}</Text>
									</Button>
								</Box>
							))
						) : (
							<Text>No items found</Text>
						))
					)}
				</VStack>
			</PopupModal>

			<VStack gap={24}>
				<Box py={16}>
					{filteredItems.map((line: any) => {
						return (
							<ItemListItem
								key={line.stockcode}
								item={line}
								variant="stocklist"
								removeItem={() => {
									removeStockcode({
										stockcode: line.stockcode,
										id: Number(catalogue.id),
									});
								}}
								showRechargeable={true}
								onRechargeableChange={handleRechargeableChange}
							/>
						);
					})}
				</Box>
			</VStack>
			{itemSearchTerm.length === 0 && catalogue?.lineCollection?.metaData?.pageCount > 1 && (
				<Box justifyContent="center" marginTop={30} alignItems="center">
					<Pagination pageCount={catalogue.lineCollection.metaData.pageCount} pageNumber={catalogue.lineCollection.metaData.pageNumber} onChangePage={setPageNumber} />
				</Box>
			)}
		</Box>
	);
}
